import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  ReactNode,
} from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/lib/hooks";
import { EntitySelectorRow } from "@/components/entitySelector/EntitySelectorRow";
import { EntitySelectorSkeleton } from "@/components/entitySelector/EntitySelectorSkeleton";
import {
  EntityCreateRow,
  EntitySelectorCreateRows,
} from "@/components/entitySelector/EntitySelectorCreateRows";
import { useInfiniteScroll } from "@/components/entitySelector/useInfiniteScroll";

export interface EntitySelectorBaseProps<T extends { id: string }> {
  entities: Map<string, T>;
  listLoading: boolean;
  fetchEntities: (search?: string) => Promise<string[]>;
  getDisplayName: (entity: T) => string;
  getSecondaryInfo?: (entity: T) => string;
  renderExtraInfo?: (entity: T) => ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: "default" | "ghost";
  size?: "default" | "sm";
  excludeId?: string;
  entityLabel?: string;
  onCreateNew?: () => void;
  createNewLabel?: string;
  resultLimit?: number;
  /** Renders the row's secondary line, taking precedence over getSecondaryInfo. */
  renderRowMeta?: (entity: T) => ReactNode;
  /** Renders a right-aligned block on the row, typically a price. */
  renderRowPrice?: (entity: T) => ReactNode;
  /** Renders a trailing value on the closed trigger, collapsing it to a single line. */
  renderTriggerValue?: (entity: T) => ReactNode;
  /** Pinned create actions at the bottom of the dropdown, replacing onCreateNew when set. */
  createRows?: EntityCreateRow[];
  /**
   * Loads the next page for the given search and resolves with its entity ids,
   * which are appended to the list.
   */
  onLoadMore?: (search?: string) => Promise<string[]>;
  /** Whether another page is available. */
  hasMore?: boolean;
  /** Whether a page fetch is currently in flight. */
  isLoadingMore?: boolean;
  /** Minimum dropdown width in pixels, letting it exceed the trigger width. */
  popoverMinWidth?: number;
  /** Entity ids shown in a separate group above the results while not searching. */
  pinnedEntityIds?: string[];
  /** Heading for the pinned group. */
  pinnedGroupLabel?: string;
}

/**
 * Manages the open state, debounced search and result ids for an entity selector.
 * Results from a superseded query are discarded so a slow response cannot overwrite
 * a newer one, and previous results stay rendered while the next fetch is in flight.
 * @param options - The entity fetcher, its loading flag and an optional page loader
 * @returns The dropdown state, the displayed entity ids, a paging handler and a close/reset handler
 */
export function useEntitySearch({
  fetchEntities,
  listLoading,
  onLoadMore,
}: {
  fetchEntities: (search?: string) => Promise<string[]>;
  listLoading: boolean;
  onLoadMore?: (search?: string) => Promise<string[]>;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const [displayedEntityIds, setDisplayedEntityIds] = useState<string[]>([]);
  const [hasLoadedResults, setHasLoadedResults] = useState(false);
  const activeQuery = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setDisplayedEntityIds([]);
      setHasLoadedResults(false);
      activeQuery.current = undefined;
      return;
    }

    const query = debouncedSearchValue || undefined;
    activeQuery.current = query;

    const loadSearchResults = async () => {
      const ids = await fetchEntities(query);
      if (activeQuery.current !== query) {
        return;
      }
      setDisplayedEntityIds(ids);
      setHasLoadedResults(true);
    };

    loadSearchResults();
  }, [debouncedSearchValue, open, fetchEntities]);

  const loadMore = useCallback(async () => {
    if (!onLoadMore) {
      return;
    }

    const queryAtRequest = activeQuery.current;
    const ids = await onLoadMore(queryAtRequest);
    if (activeQuery.current !== queryAtRequest) {
      return;
    }

    setDisplayedEntityIds((current) => {
      const known = new Set(current);
      return [...current, ...ids.filter((id) => !known.has(id))];
    });
  }, [onLoadMore]);

  const isSearching = searchValue !== debouncedSearchValue || listLoading;

  const closeSelector = useCallback(() => {
    setOpen(false);
    setSearchValue("");
  }, []);

  return {
    open,
    setOpen,
    searchValue,
    setSearchValue,
    isSearching,
    displayedEntityIds,
    hasLoadedResults,
    loadMore,
    closeSelector,
  };
}

interface EntityDropdownContentProps<T extends { id: string }> {
  entities: Map<string, T>;
  displayedEntityIds: string[];
  excludeId?: string;
  isSearching: boolean;
  hasLoadedResults: boolean;
  isItemSelected: (id: string) => boolean;
  onSelectItem: (id: string) => void;
  getDisplayName: (entity: T) => string;
  getSecondaryInfo?: (entity: T) => string;
  renderExtraInfo?: (entity: T) => ReactNode;
  searchValue: string;
  searchPlaceholder: string;
  setSearchValue: (value: string) => void;
  entityLabel: string;
  onCreateNew?: () => void;
  createNewLabel?: string;
  resultLimit?: number;
  renderRowMeta?: (entity: T) => ReactNode;
  renderRowPrice?: (entity: T) => ReactNode;
  createRows?: EntityCreateRow[];
  /** Closes the popover and clears the search, handed to each create row's handler. */
  closeSelector: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  pinnedEntityIds?: string[];
  pinnedGroupLabel?: string;
}

/**
 * Renders the searchable dropdown body shared by the single and multi entity selectors.
 * @param props - The entity data, row renderers and paging state for the dropdown
 * @returns The command palette contents for the selector popover
 */
export function EntityDropdownContent<T extends { id: string }>({
  entities,
  displayedEntityIds,
  excludeId,
  isSearching,
  hasLoadedResults,
  isItemSelected,
  onSelectItem,
  getDisplayName,
  getSecondaryInfo,
  renderExtraInfo,
  searchValue,
  searchPlaceholder,
  setSearchValue,
  entityLabel,
  onCreateNew,
  createNewLabel,
  resultLimit,
  renderRowMeta,
  renderRowPrice,
  createRows,
  closeSelector,
  onLoadMore,
  hasMore,
  isLoadingMore,
  pinnedEntityIds,
  pinnedGroupLabel,
}: EntityDropdownContentProps<T>) {
  const resolveEntities = useCallback(
    (ids: string[]) =>
      ids
        .map((id) => entities.get(id))
        .filter((entity): entity is T => entity !== undefined)
        .filter((entity) => entity.id !== excludeId),
    [entities, excludeId],
  );

  const pinnedEntities = useMemo(
    () => (searchValue ? [] : resolveEntities(pinnedEntityIds ?? [])),
    [searchValue, resolveEntities, pinnedEntityIds],
  );

  const resultEntities = useMemo(() => {
    const pinnedIds = new Set(pinnedEntities.map((entity) => entity.id));
    return resolveEntities(displayedEntityIds).filter(
      (entity) => !pinnedIds.has(entity.id),
    );
  }, [pinnedEntities, resolveEntities, displayedEntityIds]);

  const effectiveCreateRows = useMemo(
    () => buildCreateRows(createRows, onCreateNew, createNewLabel),
    [createRows, onCreateNew, createNewLabel],
  );

  const sentinelRef = useInfiniteScroll({
    onLoadMore,
    hasMore: hasMore ?? false,
    isLoading: isLoadingMore ?? false,
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const shortcutRow = effectiveCreateRows.find(
        (row) => row.shortcut === "mod+enter",
      );
      if (!shortcutRow) {
        return;
      }
      if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) {
        return;
      }
      event.preventDefault();
      shortcutRow.onSelect(searchValue, closeSelector);
    },
    [effectiveCreateRows, searchValue, closeSelector],
  );

  const renderEntityRow = (entity: T) => (
    <CommandItem
      key={entity.id}
      value={entity.id}
      onSelect={() => onSelectItem(entity.id)}
    >
      <EntitySelectorRow
        name={getDisplayName(entity)}
        query={searchValue}
        meta={
          renderRowMeta ? renderRowMeta(entity) : getSecondaryInfo?.(entity)
        }
        extraInfo={renderExtraInfo?.(entity)}
        price={renderRowPrice?.(entity)}
        isSelected={isItemSelected(entity.id)}
      />
    </CommandItem>
  );

  const showSkeletons = isSearching && !hasLoadedResults;
  const staleResultsClassName = cn(
    isSearching && hasLoadedResults && "opacity-60 transition-opacity",
  );

  return (
    <Command shouldFilter={false} onKeyDown={handleKeyDown}>
      <CommandInput
        placeholder={searchPlaceholder}
        value={searchValue}
        onValueChange={setSearchValue}
      />
      <CommandList>
        {showSkeletons ? (
          <EntitySelectorSkeleton />
        ) : (
          <>
            <CommandEmpty>
              {searchValue
                ? `No ${entityLabel} found.`
                : `No ${entityLabel} available.`}
            </CommandEmpty>
            {pinnedEntities.length > 0 && (
              <CommandGroup
                heading={pinnedGroupLabel}
                className={staleResultsClassName}
              >
                {pinnedEntities.map(renderEntityRow)}
              </CommandGroup>
            )}
            <CommandGroup className={staleResultsClassName}>
              {resultEntities.map(renderEntityRow)}
            </CommandGroup>
            {onLoadMore ? (
              <>
                <div ref={sentinelRef} aria-hidden="true" className="h-px" />
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-2 text-xs text-muted-foreground">
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Loading more
                  </div>
                )}
              </>
            ) : (
              resultLimit !== undefined &&
              displayedEntityIds.length >= resultLimit && (
                <div className="py-2 text-center text-xs text-muted-foreground">
                  Search to find more results
                </div>
              )
            )}
            <EntitySelectorCreateRows
              rows={effectiveCreateRows}
              query={searchValue}
              closeSelector={closeSelector}
            />
          </>
        )}
      </CommandList>
    </Command>
  );
}

/**
 * Resolves the create rows to render, adapting a legacy onCreateNew into a single row.
 * @param createRows - Explicit create rows, which take precedence when non-empty
 * @param onCreateNew - Legacy single create handler, run after the selector closes
 * @param createNewLabel - Legacy single create label
 * @returns The create rows to render
 */
function buildCreateRows(
  createRows: EntityCreateRow[] | undefined,
  onCreateNew: (() => void) | undefined,
  createNewLabel: string | undefined,
): EntityCreateRow[] {
  if (createRows && createRows.length > 0) {
    return createRows;
  }
  if (!onCreateNew) {
    return [];
  }
  return [
    {
      key: "create-new",
      label: () => createNewLabel ?? "Create new",
      onSelect: (_query: string, close: () => void) => {
        close();
        onCreateNew();
      },
    },
  ];
}
